'use client';

import type { JSX } from 'react';

import ProjectionLabel, { ProjectionType } from 'DesignSystem2/Projection/ProjectionLabel';
import Palette from 'DesignSystem2/Theme/Palette';

import { partyMarginColor } from '../EventNumericForecastGraph/partyMargin';
import { useGraphPayload } from '../interaction/GraphInteractionContext';
import GraphHeaderShell from './GraphHeaderShell';
import type { NumericForecastPayload } from './payloads';

const renderProjection = (payload: NumericForecastPayload): JSX.Element | undefined => {
    if (payload.mode === 'hidden') return undefined;
    if (payload.mode === 'chance') {
        return (
            <div className="mb-1">
                <ProjectionLabel
                    type={ProjectionType.CHANCE}
                    projection={payload.projection}
                    change={payload.change}
                    color={payload.vibrant ? Palette.Brand.Primary : Palette.Text.x10}
                    customLabel={payload.customLabel}
                    dontAnimate={payload.isHovering}
                    size={payload.isCompact ? 'small' : 'large'}
                    hideTooltip={payload.isCompact || payload.vibrant}
                />
            </div>
        );
    }
    return (
        <div className="mb-1" aria-label={payload.marginKind ? 'Margin forecast' : undefined}>
            <ProjectionLabel
                type={ProjectionType.FORECAST}
                projection={payload.projection}
                text={payload.text}
                customLabel={payload.marginKind ? '' : undefined}
                change={payload.topParty === 'D' ? -payload.change : payload.change}
                changeText={payload.changeText}
                color={
                    payload.marginKind === 'party'
                        ? partyMarginColor(payload.projection)
                        : payload.vibrant
                          ? Palette.Brand.Primary
                          : Palette.Text.x10
                }
                changeColor={
                    payload.marginKind === 'party'
                        ? partyMarginColor(payload.change)
                        : payload.marginKind === 'candidate'
                          ? Palette.Text.x10
                          : undefined
                }
                numberScrollAnimation={!payload.marginKind}
                customTooltipText={
                    payload.marginKind === 'party'
                        ? 'Implied margin of victory from market prices. R is a Republican lead; D is a Democratic lead. Values are percentage points.'
                        : payload.marginKind === 'candidate'
                          ? `Implied margin of victory for ${payload.candidateLabel} from market prices. Values are percentage points.`
                          : undefined
                }
                dontAnimate={payload.isHovering}
                size={payload.isCompact ? 'small' : 'large'}
                hideTooltip={payload.isCompact || payload.vibrant}
            />
        </div>
    );
};

/**
 * Header for the numeric event forecast chart. Builds either the CHANCE
 * (highlighted-market) or FORECAST `<ProjectionLabel>` from the body's
 * published `mode` discriminator.
 */
const NumericForecastHeader = () => {
    const payload = useGraphPayload<NumericForecastPayload>('numericForecast');
    if (!payload) return null;
    // Hide while genuinely unavailable OR still loading — `isLoading` keeps the
    // header collapsed during load (no flash of a `0` projection) now that
    // `chartUnavailable` no longer folds loading in (so the toggle-row logo can
    // stay visible through the load).
    if (payload.chartUnavailable || payload.isLoading) return <GraphHeaderShell />;
    return <GraphHeaderShell projection={renderProjection(payload)} />;
};

export default NumericForecastHeader;
